import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function StoriesBar() {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(null); // {author, stories, index}
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/stories");
      setGroups(r.data);
    } catch (err) {
      console.warn("Failed to load stories", err);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(null);
      if (event.key === "ArrowLeft") {
        setOpen((current) => current && current.index > 0 ? { ...current, index: current.index - 1 } : current);
      }
      if (event.key === "ArrowRight") {
        setOpen((current) => current && current.index < current.stories.length - 1 ? { ...current, index: current.index + 1 } : current);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const addStory = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const u = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/stories", { image_path: u.data.path, caption: "" });
      toast.success("Story shared (24h)");
      load();
    } catch { toast.error("Could not share story"); }
  };

  return (
    <div className="mb-6" data-testid="stories-bar">
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={addStory} data-testid="story-file-input" />
      <div className="flex gap-3 overflow-x-auto pb-2">
        <button onClick={() => fileRef.current?.click()} data-testid="add-story-btn"
          className="brutal-card flex-shrink-0 w-24 h-32 p-1 bg-white -rotate-2 hover:rotate-0 transition-transform flex flex-col items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-primary border-2 border-foreground flex items-center justify-center text-primary-foreground">
            <Plus size={20} strokeWidth={3} />
          </div>
          <span className="text-xs font-semibold mt-2">Your story</span>
        </button>
        {groups.map((g, i) => (
          <button key={g.author.id} onClick={() => setOpen({ ...g, index: 0 })} data-testid={`story-${g.author.id}`}
            aria-label={`View ${g.author.display_name || "member"}'s story`}
            className={`brutal-card flex-shrink-0 w-24 h-32 p-1 bg-white hover:rotate-0 transition-transform ${i % 2 ? "rotate-2" : "-rotate-2"}`}>
            <div className="w-full h-full rounded-lg border-2 border-foreground overflow-hidden flex flex-col">
              {g.stories[0]?.image_path ? (
                <img src={fileUrl(g.stories[0].image_path)} alt="" className="w-full h-20 object-cover" />
              ) : <div className="w-full h-20 bg-sage" />}
              <div className="px-1 text-[10px] font-semibold truncate text-center mt-1">{g.author.display_name}</div>
            </div>
          </button>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="story-viewer-title" data-testid="story-viewer">
          <button type="button" aria-label="Close story viewer" className="absolute inset-0 bg-foreground/80" onClick={() => setOpen(null)} />
          <div className="relative z-10 brutal-card bg-card max-w-md w-full p-0 overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b-2 border-foreground">
              <div id="story-viewer-title" className="font-semibold">{open.author.display_name}</div>
              <button aria-label="Close story viewer" onClick={() => setOpen(null)} className="brutal-btn bg-card p-1" data-testid="close-story-btn"><X size={16} strokeWidth={2.5} /></button>
            </div>
            {open.stories[open.index]?.image_path && (
              <img src={fileUrl(open.stories[open.index].image_path)} alt={`${open.author.display_name || "Member"} story`} className="w-full max-h-[70vh] object-contain" />
            )}
            <div className="p-3 flex justify-between text-sm items-center">
              <button disabled={open.index <= 0} onClick={() => setOpen({ ...open, index: open.index - 1 })} className="brutal-btn bg-card px-3 py-1 disabled:opacity-40">Prev</button>
              <span aria-live="polite">{open.index + 1}/{open.stories.length}</span>
              <button disabled={open.index >= open.stories.length - 1} onClick={() => setOpen({ ...open, index: open.index + 1 })} className="brutal-btn bg-card px-3 py-1 disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
